<template>
  <div class="home">
    <section id="home" class="hero">
      <div class="hero-image"></div>
      <div class="hero-overlay"></div>
      <div class="container hero-content">
        <div class="hero-copy">
          <div class="eyebrow light"><span></span> Accounting · Advisory · Growth</div>
          <h1>Clarity for your numbers. <em>Confidence</em> for your business.</h1>
          <p>We help businesses understand their finances, stay compliant and make informed decisions that support sustainable growth.</p>
          <div class="hero-actions">
            <a href="#services" class="btn btn-primary">Explore Our Services <span>→</span></a>
            <a href="#contact" class="btn btn-ghost">Book a Consultation</a>
          </div>
        </div>

        <div class="hero-note">
          <span class="note-line"></span>
          <span>Professional financial guidance<br>for businesses in Nigeria</span>
        </div>
      </div>
      <a href="#about" class="scroll-cue"><span></span> Scroll to explore</a>
    </section>

    <section class="trust-strip">
      <div class="container trust-grid">
        <div v-for="item in trustItems" :key="item.title" class="trust-item">
          <span class="trust-icon">{{ item.icon }}</span>
          <div>
            <strong>{{ item.title }}</strong>
            <small>{{ item.text }}</small>
          </div>
        </div>
      </div>
    </section>

    <section id="about" class="section about">
      <div class="container about-grid">
        <div class="about-image-wrap">
          <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=85" alt="Business professionals collaborating in a meeting">
          <div class="experience-card">
            <strong>J</strong>
            <span>Built around<br><b>clarity & trust.</b></span>
          </div>
        </div>
        <div class="about-copy">
          <div class="eyebrow"><span></span> About Jomass</div>
          <h2>More than accounting.<br><em>A partner for your business.</em></h2>
          <p class="lead">Your financial records should do more than satisfy a requirement. They should help you understand where your business stands and where it can go.</p>
          <p>Jomass Accounting Firm / Business Consultants combines accounting expertise with practical business thinking. We work with business owners, organisations and professionals to bring structure to their finances and confidence to their decisions.</p>
          <div class="about-points">
            <div><span>01</span><b>Clear financial information</b></div>
            <div><span>02</span><b>Practical business advice</b></div>
            <div><span>03</span><b>Reliable ongoing support</b></div>
          </div>
          <a href="#contact" class="text-link">Talk to our team <span>↗</span></a>
        </div>
      </div>
    </section>

    <section id="services" class="section services">
      <div class="container">
        <div class="section-heading services-heading">
          <div>
            <div class="eyebrow"><span></span> What we do</div>
            <h2>Financial expertise that<br><em>moves business forward.</em></h2>
          </div>
          <p>From everyday accounting to strategic advisory, our services are designed around the realities of running a business.</p>
        </div>

        <div class="service-grid">
          <article v-for="(service, index) in services" :key="service.title" class="service-card">
            <div class="service-top">
              <span class="service-number">0{{ index + 1 }}</span>
              <span class="service-icon">{{ service.icon }}</span>
            </div>
            <h3>{{ service.title }}</h3>
            <p>{{ service.text }}</p>
            <a href="#contact" class="card-link">Learn more <span>↗</span></a>
          </article>
        </div>
      </div>
    </section>

    <section class="statement-section">
      <div class="container statement-inner">
        <div class="statement-number">01</div>
        <div>
          <div class="eyebrow light"><span></span> Our perspective</div>
          <h2>Good financial management is not just about <em>looking back.</em></h2>
          <p>It is about having the information and advice you need to make better decisions today.</p>
        </div>
      </div>
    </section>

    <section id="industries" class="section industries">
      <div class="container">
        <div class="section-heading">
          <div>
            <div class="eyebrow"><span></span> Who we work with</div>
            <h2>Built for businesses<br><em>across industries.</em></h2>
          </div>
          <p>We adapt our financial and advisory support to the commercial realities of your industry.</p>
        </div>

        <div class="industry-grid">
          <div v-for="industry in industries" :key="industry.title" class="industry-card" :style="{ backgroundImage: `url(${industry.image})` }">
            <div class="industry-overlay"></div>
            <div class="industry-content">
              <span>{{ industry.number }}</span>
              <h3>{{ industry.title }}</h3>
              <p>{{ industry.text }}</p>
              <a href="#contact">Discuss your business <span>↗</span></a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="approach">
      <div class="container approach-grid">
        <div>
          <div class="eyebrow"><span></span> How we work</div>
          <h2>A straightforward approach to <em>complex numbers.</em></h2>
          <p>We keep our process collaborative, practical and easy to understand.</p>
        </div>
        <div class="steps">
          <div v-for="(step, index) in steps" :key="step.title" class="step">
            <span>0{{ index + 1 }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="insights" class="section insights">
      <div class="container">
        <div class="section-heading">
          <div>
            <div class="eyebrow"><span></span> Jomass Insights</div>
            <h2>Useful thinking for<br><em>business owners.</em></h2>
          </div>
          <a href="#contact" class="text-link">View all insights <span>↗</span></a>
        </div>

        <div class="insight-grid">
          <article v-for="post in insights" :key="post.title" class="insight-card">
            <div class="insight-image">
              <img :src="post.image" :alt="post.title">
              <span>{{ post.category }}</span>
            </div>
            <div class="insight-body">
              <small>{{ post.date }}</small>
              <h3>{{ post.title }}</h3>
              <a href="#contact">Read article <span>→</span></a>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section id="contact" class="contact">
      <div class="contact-image"></div>
      <div class="contact-overlay"></div>
      <div class="container contact-inner">
        <div class="contact-copy">
          <div class="eyebrow light"><span></span> Start a conversation</div>
          <h2>Let's make your numbers work <em>for your business.</em></h2>
          <p>Whether you need ongoing accounting support, tax guidance or a fresh perspective on your business, we're ready to listen.</p>
        </div>
        <form class="contact-form" @submit.prevent="submitForm">
          <div class="form-row">
            <label>
              <span>Your name</span>
              <input v-model="form.name" type="text" placeholder="Full name" required>
            </label>
            <label>
              <span>Business email</span>
              <input v-model="form.email" type="email" placeholder="you@company.com" required>
            </label>
          </div>
          <label>
            <span>What can we help with?</span>
            <select v-model="form.service" required>
              <option value="" disabled>Select a service</option>
              <option>Accounting & Bookkeeping</option>
              <option>Audit & Assurance</option>
              <option>Tax & Compliance</option>
              <option>Business Consulting</option>
              <option>Financial Advisory</option>
              <option>Other</option>
            </select>
          </label>
          <label>
            <span>Tell us a little about your needs</span>
            <textarea v-model="form.message" rows="4" placeholder="How can Jomass help?" required></textarea>
          </label>
          <button class="btn btn-primary" type="submit">Request a Consultation <span>→</span></button>
          <p v-if="submitted" class="form-success">Thank you. Your consultation request has been received.</p>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      submitted: false,
      form: {
        name: '',
        email: '',
        service: '',
        message: ''
      },
      trustItems: [
        { icon: '◎', title: 'Accounting', text: 'Accurate & organised records' },
        { icon: '◫', title: 'Tax', text: 'Compliance & advisory' },
        { icon: '◌', title: 'Audit', text: 'Confidence in your numbers' },
        { icon: '↗', title: 'Advisory', text: 'Decisions backed by insight' }
      ],
      services: [
        { icon: '₦', title: 'Accounting & Bookkeeping', text: 'Reliable records, reporting and financial organisation that give you a clearer view of your business.' },
        { icon: '◫', title: 'Audit & Assurance', text: 'Independent, structured review that strengthens confidence in financial information and controls.' },
        { icon: '⌁', title: 'Tax & Compliance', text: 'Practical tax planning and compliance support designed around your business obligations.' },
        { icon: '↗', title: 'Business Consulting', text: 'Objective advice to help you improve processes, manage risk and make sound business decisions.' },
        { icon: '◈', title: 'Financial Advisory', text: 'Financial analysis, planning and insight to support growth, investment and strategic decisions.' },
        { icon: '□', title: 'Payroll & Controls', text: 'Structured payroll and internal financial controls that help your operations run with confidence.' }
      ],
      industries: [
        { number: '01', title: 'Oil & Gas', text: 'Financial support for a complex and highly regulated sector.', image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1000&q=80' },
        { number: '02', title: 'Real Estate', text: 'Better financial visibility across property and development.', image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1000&q=80' },
        { number: '03', title: 'Construction', text: 'Financial structure for projects, costs and growth.', image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1000&q=80' },
        { number: '04', title: 'SMEs & Startups', text: 'Practical support for businesses building their foundations.', image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1000&q=80' }
      ],
      steps: [
        { title: 'Understand', text: 'We listen, review your situation and identify what matters most.' },
        { title: 'Plan', text: 'We turn the numbers and requirements into a clear practical plan.' },
        { title: 'Execute', text: 'We put the agreed processes, reporting or advisory work into action.' },
        { title: 'Support', text: 'We remain available as your business changes and grows.' }
      ],
      insights: [
        { category: 'Tax', date: 'Business guide · 6 min read', title: 'What business owners should know about staying tax compliant', image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=1000&q=80' },
        { category: 'Finance', date: 'Business guide · 5 min read', title: 'Five financial reports every growing business should understand', image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1000&q=80' },
        { category: 'Advisory', date: 'Business guide · 7 min read', title: 'From numbers to decisions: making your accounts useful', image: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1000&q=80' }
      ]
    }
  },
  methods: {
    submitForm() {
      this.submitted = true
      setTimeout(() => { this.submitted = false }, 6000)
    }
  }
}
</script>